import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User, Customer, TeamMember, ActivityLog, ToastMessage } from '../types/interfaces';

// ============================================================================
// AUTH CONTEXT
// ============================================================================
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug: User-Status bei jedem Render
  console.log('[AuthProvider] Render: user =', user);

  // Debug: User-Status beim Setzen
  const debugSetUser = (u: User | null) => {
    console.log('[AuthProvider] setUser:', u);
    setUser(u);
  };

  const logout = useCallback(() => {
    debugSetUser(null);
    setError(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, setUser: debugSetUser, setLoading, setError, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// ============================================================================
// DATA CONTEXT
// ============================================================================
interface DataContextType {
  customers: Customer[];
  teamMembers: TeamMember[];
  setCustomers: (customers: Customer[]) => void;
  setTeamMembers: (members: TeamMember[]) => void;
  addCustomer: (customer: Customer) => void;
  updateCustomer: (customer: Customer) => void;
  deleteCustomer: (id: string) => void;
  addTeamMember: (member: TeamMember) => void;
  updateTeamMember: (member: TeamMember) => void;
  deleteTeamMember: (id: string) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const addCustomer = useCallback((customer: Customer) => {
    setCustomers(prev => [...prev, customer]);
  }, []);

  const updateCustomer = useCallback((customer: Customer) => {
    setCustomers(prev => prev.map(c => c.id === customer.id ? customer : c));
  }, []);

  const deleteCustomer = useCallback((id: string) => {
    setCustomers(prev => prev.filter(c => c.id !== id));
  }, []);

  const addTeamMember = useCallback((member: TeamMember) => {
    setTeamMembers(prev => [...prev, member]);
  }, []);

  const updateTeamMember = useCallback((member: TeamMember) => {
    setTeamMembers(prev => prev.map(m => m.id === member.id ? member : m));
  }, []);

  const deleteTeamMember = useCallback((id: string) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  }, []);

  return (
    <DataContext.Provider
      value={{
        customers,
        teamMembers,
        setCustomers,
        setTeamMembers,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        addTeamMember,
        updateTeamMember,
        deleteTeamMember,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within DataProvider');
  return context;
};

// ============================================================================
// UI CONTEXT
// ============================================================================
interface UIContextType {
  toasts: ToastMessage[];
  addToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  removeToast: (index: number) => void;
  clearToasts: () => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToasts(prev => [...prev, { message, type, duration: 3000 }]);
  }, []);

  const removeToast = useCallback((index: number) => {
    setToasts(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <UIContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within UIProvider');
  return context;
};

// ============================================================================
// COMBINED CONTEXT HOOK
// ============================================================================
export const useAppContexts = () => {
  return {
    auth: useAuth(),
    data: useData(),
    ui: useUI(),
  };
};
